import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { colors, spacing, typography, borderRadius, breakpoints } from '../styles/theme';
import type { EventMarkerData } from './EventMap';
import { toEventShape } from './EventMap';
import { getEventLocalSortKey } from '../utils/eventDateTime';
import { getEventDateTimeDisplay, getVenueLabel } from '../utils/eventPresentation';
import { getProximity, getVibeStyle, PROXIMITY_LABELS, PROXIMITY_OPACITY, VIBE_STYLES, DEFAULT_VIBE_STYLE } from '../utils/eventMapStyles';
import { buildEventPath } from '../utils/slug';
import RememberButton from './events/RememberButton';

/**
 * Scrollable, date-ordered companion list for the map. Hovering a row
 * highlights and centres the matching pin; every marker therefore has an
 * accessible, keyboard-reachable equivalent.
 */

const Panel = styled.aside`
  display: flex;
  flex-direction: column;
  /* Must be height-constrained, otherwise the flex column grows to fit all
     rows and ScrollArea never scrolls. */
  height: 100%;
  min-height: 0;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.xl};
  background: ${colors.white};
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${colors.border.light};
  background: ${colors.background.secondary};
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
`;

const PanelHint = styled.p`
  margin: 2px 0 0;
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
`;

const ScrollArea = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  scrollbar-width: thin;
`;

const Row = styled.li.withConfig({
  shouldForwardProp: (prop) => prop !== 'isHighlighted' && prop !== 'accent' && prop !== 'dim',
})<{ isHighlighted: boolean; accent: string; dim: number }>`
  position: relative;
  border-bottom: 1px solid ${colors.border.light};
  border-left: 4px solid ${(props) => (props.isHighlighted ? props.accent : 'transparent')};
  background: ${(props) => (props.isHighlighted ? colors.primary[50] : colors.white)};
  opacity: ${(props) => (props.isHighlighted ? 1 : props.dim)};
  transition: background 0.15s ease, opacity 0.15s ease;

  &:last-child {
    border-bottom: none;
  }
`;

const MapRememberButton = styled(RememberButton)`
  position: absolute;
  top: ${spacing[2]};
  right: ${spacing[2]};
  z-index: 1;
`;

const RowLink = styled(Link)`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: ${spacing[2]};
  align-items: start;
  padding: ${spacing[3]} ${spacing[16]} ${spacing[3]} ${spacing[4]};
  min-height: 44px;
  text-decoration: none;
  color: inherit;

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: -2px;
  }
`;

const Dot = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'accent',
})<{ accent: string }>`
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 50%;
  background: ${(props) => props.accent};
  flex-shrink: 0;
`;

const RowBody = styled.div`
  min-width: 0;
`;

const RowTitle = styled.div`
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  line-height: ${typography.lineHeights.tight};
  overflow-wrap: anywhere;
`;

const RowMeta = styled.div`
  margin-top: 2px;
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
  overflow-wrap: anywhere;
`;

const Legend = styled.div`
  padding: ${spacing[2]} ${spacing[4]};
  border-top: 1px solid ${colors.border.light};
  background: ${colors.background.secondary};
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1]} ${spacing[3]};
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const LegendSwatch = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'accent' && prop !== 'swatchOpacity',
})<{ accent: string; swatchOpacity: number }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => props.accent};
  opacity: ${(props) => props.swatchOpacity};
`;

const EmptyRow = styled.li`
  padding: ${spacing[6]} ${spacing[4]};
  text-align: center;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

const SkeletonRow = styled.li`
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr);
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${colors.border.light};

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
    }
  }
`;

const SkeletonDot = styled.span`
  width: 10px;
  height: 10px;
  margin-top: 3px;
  border-radius: ${borderRadius.full};
  background: ${colors.gray[200]};
`;

const SkeletonBody = styled.div`
  display: grid;
  gap: ${spacing[2]};
`;

const SkeletonLine = styled.span`
  display: block;
  width: 82%;
  height: 12px;
  border-radius: ${borderRadius.sm};
  background: linear-gradient(
    90deg,
    ${colors.gray[100]} 25%,
    ${colors.gray[200]} 50%,
    ${colors.gray[100]} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;

  &:last-child {
    width: 54%;
    height: 10px;
  }
`;

const MobileNote = styled.p`
  display: none;
  margin: 0;
  padding: ${spacing[2]} ${spacing[4]};
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};

  @media (max-width: ${breakpoints.lg}) {
    display: block;
  }
`;

export interface MapEventListProps {
  events: EventMarkerData[];
  highlightedEventId: number | null;
  onHighlightChange: (eventId: number | null) => void;
  isLoading?: boolean;
}

export const MapEventList: React.FC<MapEventListProps> = ({
  events,
  highlightedEventId,
  onHighlightChange,
  isLoading = false,
}) => {
  const ordered = React.useMemo(
    () =>
      [...events].sort(
        (a, b) =>
          (getEventLocalSortKey(a.startDate) ?? Number.MAX_SAFE_INTEGER) -
          (getEventLocalSortKey(b.startDate) ?? Number.MAX_SAFE_INTEGER),
      ),
    [events],
  );

  return (
    <Panel aria-label="Events on the map" aria-busy={isLoading}>
      <PanelHeader>
        <PanelTitle>
          {isLoading ? 'Finding nearby events' : `${ordered.length} event${ordered.length === 1 ? '' : 's'}`}
        </PanelTitle>
        <PanelHint>
          {isLoading ? 'Loading the closest results first' : 'Soonest first — hover a row to locate it on the map'}
        </PanelHint>
      </PanelHeader>
      <MobileNote>Tap a row to open the event.</MobileNote>

      <ScrollArea>
        {isLoading && Array.from({ length: 6 }, (_, index) => (
          <SkeletonRow key={index} aria-hidden="true">
            <SkeletonDot />
            <SkeletonBody>
              <SkeletonLine />
              <SkeletonLine />
            </SkeletonBody>
          </SkeletonRow>
        ))}
        {!isLoading && ordered.length === 0 && <EmptyRow>No events in this area.</EmptyRow>}
        {!isLoading && ordered.map((event) => {
          const shaped = toEventShape(event);
          const when = getEventDateTimeDisplay(shaped);
          const style = getVibeStyle(shaped);
          const proximity = getProximity(shaped);
          const path = buildEventPath(event);
          const isHighlighted = highlightedEventId === event.eventId;
          const venue = getVenueLabel(shaped);
          const place = [venue, event.city].filter(Boolean).join(' · ');

          return (
            <Row
              key={event.eventId}
              data-event-id={event.eventId}
              tabIndex={-1}
              isHighlighted={isHighlighted}
              accent={style.color}
              dim={PROXIMITY_OPACITY[proximity]}
              onMouseEnter={() => onHighlightChange(event.eventId)}
              onMouseLeave={() => onHighlightChange(null)}
            >
              <RowLink
                to={path || '#'}
                onFocus={() => onHighlightChange(event.eventId)}
                onBlur={() => onHighlightChange(null)}
                title={`${event.title} — ${PROXIMITY_LABELS[proximity]}`}
              >
                <Dot accent={style.color} aria-hidden="true" />
                <RowBody>
                  <RowTitle>{event.title}</RowTitle>
                  <RowMeta>
                    {[when.dateLabel, when.timeLabel].filter(Boolean).join(' · ')}
                  </RowMeta>
                  {place && <RowMeta>{place}</RowMeta>}
                </RowBody>
              </RowLink>
              <MapRememberButton eventId={event.eventId} actionLocation="map_companion_list" compact />
            </Row>
          );
        })}
      </ScrollArea>

      <Legend aria-label="Map legend">
        {VIBE_STYLES.map((style) => (
          <LegendItem key={style.key}>
            <LegendSwatch accent={style.color} swatchOpacity={1} />
            {style.label}
          </LegendItem>
        ))}
        <LegendItem>
          <LegendSwatch accent={DEFAULT_VIBE_STYLE.color} swatchOpacity={1} />
          {DEFAULT_VIBE_STYLE.label}
        </LegendItem>
        <LegendItem>
          <LegendSwatch accent={colors.gray[700]} swatchOpacity={PROXIMITY_OPACITY.month} />
          Faded = further out
        </LegendItem>
      </Legend>
    </Panel>
  );
};

export default MapEventList;
